import Icon from "../Icon/Icon"
import React, {Dispatch, Fragment, SetStateAction, useCallback, useEffect, useState} from "react"
import classNames from "classnames"
import {
	ACTIVITY_REASONS,
	DOCUMENT_REASONS,
	EntityDeclineReason,
	EvaluationOutcome,
	IDENTITY_REASONS,
	REASON_CODES_TO_MESSAGE,
} from "../../resources/application"
import {ErrorDocument} from "../../resources/common"
import Chips from "../Chips/Chips"
import {GridContainer} from "../../containers/GridContainer/GridContainer"
import {AsyncResultIdleRequestState} from "../../types/asyncResultIdle"
import {Err} from "../Err/Err"

export interface DenyConfig {
	initialReasons: Map<string, string[]>
	evaluationOutcome: Map<string, EvaluationOutcome>
	isMultiEntity: boolean
	entityName: string
	denyFn: (reason: string, declineReasons: EntityDeclineReason[]) => void
}

export function getSingleEntityReasons(
	reasons: Map<string, string[]>,
	entityName: string,
	reasonCodes?: string[],
	outcome?: EvaluationOutcome
) {
	reasons.set(
		entityName,
		reasonCodes && outcome != EvaluationOutcome.Approved
			? Array.from(
					new Set(
						reasonCodes.map((code) => REASON_CODES_TO_MESSAGE.get(code)).filter((message) => message != undefined)
					) as Set<string>
			  )
			: []
	)
}

export function ApplicationDenyContent<T>({
	config,
	onSuccess,
	state,
}: {
	config: DenyConfig
	onSuccess: () => void
	state: AsyncResultIdleRequestState<T, ErrorDocument>
}) {
	const {initialReasons, evaluationOutcome} = config
	const [allReasons, setReasons] = useState(initialReasons)
	const [isLoading, setIsLoading] = useState(false)
	const [currentEntity, setCurrentEntity] = useState<string | undefined>(undefined)
	const [reason, setReason] = useState("")

	const setEntity = useCallback(
		(entity: string, declineReason: string) => {
			setReasons((prev) => {
				const old = prev.get(entity) ?? []
				if (old.includes(declineReason)) {
					return new Map<string, string[]>(
						prev.set(
							entity,
							old.filter((existing) => existing != declineReason)
						)
					)
				} else {
					return new Map<string, string[]>(prev.set(entity, old.concat(declineReason)))
				}
			})
		},
		[allReasons, setReasons]
	)

	const getDeclineReasons = useCallback((): EntityDeclineReason[] => {
		return Array.from(allReasons.entries())
			.map(([entity, reasons]) => ({
				entity,
				reasons,
			}))
			.filter((entry) => entry.reasons.length > 0)
	}, [allReasons])

	useEffect(() => {
		state.match(
			() => null,
			() => setIsLoading(true),
			() => {
				setIsLoading(false)
				onSuccess()
			},
			() => setIsLoading(false)
		)
	}, [state])

	return (
		<div className="application-deny">
			{currentEntity ? (
				<ApplicationDenyEntityPage
					entity={currentEntity}
					setCurrentEntity={setCurrentEntity}
					setEntity={(declineReason) => setEntity(currentEntity, declineReason)}
					selectedReasons={allReasons.get(currentEntity) ?? ([] as string[])}
					initialReasons={initialReasons.get(currentEntity) ?? ([] as string[])}
					evaluationOutcome={evaluationOutcome.get(currentEntity)}
				/>
			) : (
				<DenyMainPage
					entityName={config.entityName}
					isMultiEntity={config.isMultiEntity}
					initialReasons={initialReasons}
					selectedReasons={allReasons}
					evaluationOutcome={evaluationOutcome}
					setReason={setReason}
					reason={reason}
					setEntity={setEntity}
					setCurrentEntity={setCurrentEntity}
					denyApplication={() => config.denyFn(reason, getDeclineReasons())}
					isLoading={isLoading}
					state={state}
				/>
			)}
		</div>
	)
}

function ApplicationDenyEntityPage(props: {
	entity: string
	setEntity: (reason: string) => void
	initialReasons: string[]
	selectedReasons: string[]
	evaluationOutcome?: EvaluationOutcome
	setCurrentEntity: Dispatch<SetStateAction<string | undefined>>
}) {
	return (
		<>
			<div className="decline-reasons-header">
				<div className="application-deny-title">
					<span>Decline Reasons for</span>
					<div className="entity-title">
						<div className={classNames("entity-outcome", getOutcomeClassname(props.evaluationOutcome))}></div>
						<div className="entity-name">{props.entity}</div>
					</div>
				</div>
			</div>
			<IndividualSingleEntity
				initialReasons={props.initialReasons}
				reasons={props.selectedReasons}
				setReason={props.setEntity}
			/>
			<div className="full-divider" />
			<div className="application-deny-bottom">
				<button className="button is-black" onClick={(_) => props.setCurrentEntity(undefined)}>
					Done
				</button>
			</div>
		</>
	)
}

function DenyMainPage<T>(props: {
	entityName: string
	isMultiEntity: boolean
	setEntity: (entity: string, reason: string) => void
	selectedReasons: Map<string, string[]>
	initialReasons: Map<string, string[]>
	evaluationOutcome: Map<string, EvaluationOutcome>
	reason: string
	setReason: Dispatch<SetStateAction<string>>
	setCurrentEntity: Dispatch<SetStateAction<string | undefined>>
	denyApplication: () => void
	isLoading: boolean
	state: AsyncResultIdleRequestState<T, ErrorDocument>
}) {
	const errorContent = props.state.match(
		() => <></>,
		() => <></>,
		() => <></>,
		(err) => <Err err={err.errors[0].title}></Err>
	)

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault()
				props.denyApplication()
			}}
		>
			<div className="application-deny-title">Decline Reasons</div>
			{props.isMultiEntity ? (
				<MultiEntity
					reasons={props.selectedReasons}
					evaluationOutcome={props.evaluationOutcome}
					setCurrentEntity={props.setCurrentEntity}
				/>
			) : (
				<IndividualSingleEntity
					initialReasons={props.initialReasons.get(props.entityName) ?? ([] as string[])}
					reasons={props.selectedReasons.get(props.entityName) ?? ([] as string[])}
					setReason={(reason) => props.setEntity(props.entityName, reason)}
				/>
			)}
			<div className="full-divider" />
			<div className="field decline-notes">
				<textarea
					required
					className="textarea"
					placeholder="Notes (external)"
					value={props.reason}
					onChange={(e) => props.setReason(e.target.value)}
				/>
			</div>
			<div className="application-deny-bottom">
				<input type="submit" disabled={props.isLoading} className="button is-danger" value="Deny application" />
				<div className="decline-note">
					<Icon icon="alert-circle--interface-essential" size={16} />
					<span>Note that decline reasons will be exposed to the client</span>
				</div>
			</div>
			{errorContent}
		</form>
	)
}

function IndividualSingleEntity({
	setReason,
	reasons,
	initialReasons,
}: {
	setReason: (reason: string) => void
	reasons: string[]
	initialReasons: string[]
}) {
	return (
		<>
			<IndividualReasons initialReasons={initialReasons} selectedReasons={reasons} setSelected={setReason} />
		</>
	)
}

function MultiEntity({
	reasons,
	evaluationOutcome,
	setCurrentEntity,
}: {
	reasons: Map<string, string[]>
	evaluationOutcome: Map<string, EvaluationOutcome>
	setCurrentEntity: Dispatch<SetStateAction<string | undefined>>
}) {
	return (
		<div className="application-entities-list">
			{Array.from(reasons.keys()).map((entityName) => (
				<div className="application-entity" key={entityName}>
					<div className="entity-header">
						<div className="entity-title">
							<div
								className={classNames("entity-outcome", getOutcomeClassname(evaluationOutcome.get(entityName)))}
							></div>
							<div className="entity-name">{entityName}</div>
						</div>
						<div className="entity-edit-icon" onClick={(_) => setCurrentEntity(entityName)}>
							<Icon icon={"pencil-1--interface-essential"} size={16} />{" "}
						</div>
					</div>
					{reasons.get(entityName)?.length == 0 && evaluationOutcome.get(entityName) == EvaluationOutcome.Approved ? (
						<div className="auto-approved">Auto-approved</div>
					) : (
						<div className="reasons-section">
							<Chips labels={reasons.get(entityName) ?? []} readonly={true} />
						</div>
					)}
				</div>
			))}
		</div>
	)
}

function IndividualReasons({
	initialReasons,
	selectedReasons,
	setSelected,
}: {
	initialReasons: string[]
	selectedReasons: string[]
	setSelected: (reason: string) => void
}) {
	const documentReasons = DOCUMENT_REASONS.filter((reason) => !initialReasons.includes(reason))
	const identityReasons = IDENTITY_REASONS.filter((reason) => !initialReasons.includes(reason))
	const activityReasons = ACTIVITY_REASONS.filter((reason) => !initialReasons.includes(reason))

	return (
		<Fragment>
			<div className="all-reasons-section">
				{initialReasons.length > 0 ? (
					<div className="reasons-section">
						<div className="reason-subtitle">Selected based on Evaluation Flags:</div>
						<Chips labels={initialReasons} selectedChips={selectedReasons} setSelected={setSelected} />
					</div>
				) : null}
				<div className="reason-subtitle">
					{initialReasons.length > 0 ? "All Other reasons:" : "Select all relevant reasons:"}
				</div>
				<GridContainer gap="20px" className="reasons-section">
					<div>
						<div className="section-title"> Documents</div>
						<Chips labels={documentReasons} selectedChips={selectedReasons} setSelected={setSelected} />
					</div>
					<div>
						<div className="section-title"> Identity</div>
						<Chips labels={identityReasons} selectedChips={selectedReasons} setSelected={setSelected} />
					</div>
					<div>
						<div className="section-title"> Activity</div>
						<Chips labels={activityReasons} selectedChips={selectedReasons} setSelected={setSelected} />
					</div>
				</GridContainer>
			</div>
		</Fragment>
	)
}

function getOutcomeClassname(outcome?: EvaluationOutcome) {
	if (outcome) {
		if (outcome == EvaluationOutcome.Approved) {
			return "approved"
		}
		if (outcome == EvaluationOutcome.Denied) {
			return "denied"
		}
		if (outcome == EvaluationOutcome.PendingReview) {
			return "pending-review"
		}
	}
	return
}
